import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import Button from "@/shared/ui/button/Button";

type Props = {
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  maxInitialPages?: number;
  onPageRequest?: (page: number) => Promise<void>;
  className?: string;
  isPending?: boolean;
};

export default function Pagination({
  currentPage,
  totalPages,
  setCurrentPage,
  maxInitialPages = 4,
  onPageRequest,
  className,
  isPending = false,
}: Props) {
  const { t } = useTranslation();

  const windowSize = useMemo(
    () => Math.max(1, Math.min(maxInitialPages, totalPages)),
    [maxInitialPages, totalPages],
  );

  const pages = useMemo(() => {
    const result: Array<number | string> = [];

    if (totalPages <= windowSize) {
      for (let i = 1; i <= totalPages; i += 1) {
        result.push(i);
      }
      return result;
    }

    const chunkStart = 1 + Math.floor((currentPage - 1) / windowSize) * windowSize;
    const chunkEnd = Math.min(chunkStart + windowSize - 1, totalPages);

    if (chunkStart > 1) {
      result.push(1);
      if (chunkStart > 2) result.push("...");
    }

    for (let i = chunkStart; i <= chunkEnd; i += 1) {
      result.push(i);
    }

    if (chunkEnd < totalPages) {
      if (chunkEnd < totalPages - 1) result.push("...");
      result.push(totalPages);
    }

    return result;
  }, [currentPage, totalPages, windowSize]);

  const goTo = async (page: number): Promise<void> => {
    if (isPending) return;

    const next = Math.max(1, Math.min(totalPages, page));
    if (next === currentPage) return;

    setCurrentPage(next);

    if (onPageRequest) {
      await onPageRequest(next);
    }
  };

  return (
    <div className={cn("flex gap-1", className)}>
      <Button
        variant="solid"
        className={clsx(
          "w-16 h-9.5 p-0 text-14 font-medium capitalize bg-dark-800 hover:bg-dark-700 transition-colors duration-200",
          (currentPage === 1 || isPending) && "text-white/50 pointer-events-none",
        )}
        onClick={() => goTo(currentPage - 1)}
      >
        {t("common.prev")}
      </Button>

      {pages.map((item, idx) =>
        item === "..." ? (
          <Button
            variant="solid"
            // eslint-disable-next-line react/no-array-index-key
            key={`ellipsis-${idx}`}
            className="size-9.5 p-0 bg-dark-800 pointer-events-none"
          >
            ...
          </Button>
        ) : (
          <Button
            key={item}
            className={clsx(
              "size-9.5 p-0 select-none",
              item !== currentPage
                ? "bg-dark-800 hover:bg-dark-700 transition-colors duration-200"
                : "pointer-events-none",
              isPending && "pointer-events-none opacity-70",
            )}
            variant={item !== currentPage ? "solid" : "solid-gradient"}
            onClick={() => goTo(item as number)}
          >
            {item}
          </Button>
        ),
      )}

      <Button
        variant="solid"
        className={clsx(
          "w-16 h-9.5 p-0 text-14 font-medium capitalize bg-dark-800 hover:bg-dark-700 transition-colors duration-200",
          (currentPage === totalPages || isPending) && "text-white/50 pointer-events-none",
        )}
        onClick={() => goTo(currentPage + 1)}
      >
        {t("common.next")}
      </Button>
    </div>
  );
}
